import { ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild } from "@angular/core";
import { MagicDeckLayerComponent } from "./magic-deck-layer.component";
import { MagicDeckBannerComponent } from "./magic-deck-banner.component";
import { NgClass } from "@angular/common";
import { getScreenBreakpoint, ScreenBreakpoint } from "@ponder-shared/utils/screenBreakpoints";
import { DeckLayerName, PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DeckCard, DeckCardTagType, GameType } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { AuthService } from "@ponder-core/auth/auth.service";
import { DeckFocusedCardComponent } from "@ponder-features/user/decks/deck/deck-focused-card.component";
import { DeckTabsComponent } from "@ponder-features/user/decks/deck/tools/deck-tabs.component";
import { DeckBaseComponent } from "@ponder-features/user/decks/deck/deck-base.component";
import { MagicManaCurveChartComponent } from "./charts/magic-mana-curve-chart.component";
import { MagicColorPieChartComponent } from "./charts/magic-color-pie-chart.component";
import { MagicManaProducedPieChartComponent } from "./charts/magic-mana-produced-pie-chart.component";
import { MagicHandSimulatorComponent } from "./hand/magic-hand-simulator.component";
import { SmoothSlider } from "@ponder-shared/utils/smoothSlider";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "magic-deck",
    imports: [
        MagicDeckBannerComponent,
        DeckFocusedCardComponent,
        DeckTabsComponent,
        MagicManaCurveChartComponent,
        MagicColorPieChartComponent,
        MagicManaProducedPieChartComponent,
        MagicHandSimulatorComponent,
        NgClass
    ],
    templateUrl: "./magic-deck.component.html"
})
export class MagicDeckComponent extends DeckBaseComponent {

    @ViewChild("deckContents") private deckContentsRef!: ElementRef<HTMLElement>;
    @ViewChild("focusedCardContainer") private focusedCardContainerRef!: ElementRef<HTMLElement>;

    private smoothSlider?: SmoothSlider;

    public constructor(events: EventBroker, ponderDataClient: PonderDataClient, auth: AuthService, cdr: ChangeDetectorRef, navigation: NavigationService) {
        super(GameType.Magic, events, ponderDataClient, auth, cdr, navigation);
    }

    public override ngAfterViewInit(): void {
        this.initializeLayers();
        this.setContentsWidth();

        super.ngAfterViewInit();

        if (this.focusedCardContainerRef) {
            this.smoothSlider = new SmoothSlider(this.focusedCardContainerRef.nativeElement, this.deckContentsRef.nativeElement, 119, 250);     // 199 = 99 from header + 20 gap
        }
    }

    protected override onScreenResize(): void {
        super.onScreenResize();
        this.setContentsWidth();
    }

    private setContentsWidth(): void {
        var width = "400px";

        switch (getScreenBreakpoint()) {
            case ScreenBreakpoint.Mobile:
                width = "400px";
                break;

            case ScreenBreakpoint.Small:
            case ScreenBreakpoint.Medium:
                width = "600px";
                break;

            case ScreenBreakpoint.Large:
            case ScreenBreakpoint.XLarge:
                width = "800px";
                break;

            case ScreenBreakpoint.XXLarge:
                width = "1175px";
                break;
        }

        this.deckContentsRef.nativeElement.style.setProperty("min-width", width);
    }

    private initializeLayers(): void {
        if (!this.deck.layers) {
            return;
        }

        this.deck.layers.forEach(l => {
            var layerRef = this.deckLayersVcr.createComponent(MagicDeckLayerComponent);

            // ... todo dont do it like this anymore, do it in the html not here ...
            layerRef.instance.isOwnDeck = this.isOwnDeck;
            layerRef.instance.name = l.name as DeckLayerName;
            layerRef.instance.deckComponent = this;
            layerRef.instance.deck = this.deck;
            layerRef.instance.deckLayer = l;
            layerRef.instance.isVisible = l === this.currentLayer;

            this.deckLayers.set(layerRef.instance.name, layerRef.instance);

            if (l === this.currentLayer) {
                this.currentLayerComponent = layerRef.instance;
            }
        });
    }

    protected override onAuthed(): void {
        // ... todo when the todo above is fixed this wont be needed anymore ...
        this.deckLayers.forEach(l => {
            l.isOwnDeck = this.isOwnDeck;
        });
    }

    protected override getInitialFocusedCardId(): string | undefined {
        if (this.deck.type === "Commander") {
            return this.getFirstWithTag(DeckCardTagType.Commander)?.card?.item?.id;
        } else if (this.deck.type === "Oathbreaker") {
            return this.getFirstWithTag(DeckCardTagType.Oathbreaker)?.card?.item?.id;
        }

        return undefined;
    }

    private getFirstWithTag(tag: DeckCardTagType): DeckCard | undefined {
        var match: DeckCard | undefined = undefined;

        this.deck.layers?.forEach(l => {
            if (match) {
                return;
            }

            l.cards?.forEach(c => {
                if (match) {
                    return;
                }

                if (c.tag == tag) {
                    match = c;
                    return;
                }
            })
        })

        return match;
    }

    @HostListener("window:scroll", ["$event"])
    @HostListener('window:resize', ['$event'])
    private onWindowChange(): void {
        this.smoothSlider?.onWindowChange();
    }
}